import { z } from 'zod'

/** Prüfungen laufen an der Grenze – Server und Client benutzen dieselben Schemata. */

export const uuid = z.string().uuid()
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format JJJJ-MM-TT')

export const loginSchema = z.object({
  email: z.string().email('Keine gültige E-Mail-Adresse'),
  password: z.string().min(8, 'Mindestens 8 Zeichen'),
  /** Optional: wenn der Benutzer in mehreren Betrieben ist. */
  tenantId: uuid.optional(),
  deviceId: uuid.optional(),
  deviceLabel: z.string().max(120).optional()
})
export type LoginInput = z.infer<typeof loginSchema>

export const customerInputSchema = z.object({
  id: uuid.optional(), // vom Client erzeugt, damit Folgeaktionen darauf verweisen können
  name: z.string().min(1, 'Name oder Firma fehlt').max(200),
  contact: z.string().max(200).nullish(),
  street: z.string().max(200).nullish(),
  zip: z.string().max(20).nullish(),
  city: z.string().max(120).nullish(),
  email: z.string().email().nullish().or(z.literal('')),
  phone: z.string().max(60).nullish(),
  notes: z.string().max(2000).nullish(),
  oneOff: z.boolean().default(false)
})
export type CustomerInput = z.infer<typeof customerInputSchema>

export const projectInputSchema = z.object({
  id: uuid.optional(),
  customerId: uuid,
  name: z.string().min(1, 'Projektbezeichnung fehlt').max(200),
  street: z.string().max(200).nullish(),
  zip: z.string().max(20).nullish(),
  city: z.string().max(120).nullish(),
  notes: z.string().max(2000).nullish()
})
export type ProjectInput = z.infer<typeof projectInputSchema>

export const catalogItemInputSchema = z.object({
  id: uuid.optional(),
  code: z.string().max(40).default(''),
  name: z.string().min(1).max(300),
  unit: z.string().max(20).default('Std.'),
  unitPriceRp: z.number().int().min(0),
  vatRate: z.number().min(0).max(100).default(8.1),
  kind: z.enum(['arbeit', 'material']).default('arbeit')
})
export type CatalogItemInput = z.infer<typeof catalogItemInputSchema>

/**
 * Plausibilitätsprüfung für Rapporte – serverseitig erzwungen.
 * Ein Rapport mit Datum in der Zukunft oder 30 Stunden an einem Tag wird
 * abgewiesen, nicht stillschweigend übernommen.
 */
export const rapportLineSchema = z.object({
  kind: z.enum(['stunden', 'material']),
  /**
   * Fehlt sie, ist die Zeile Freitext. Das ist der ganze Unterschied –
   * Freitext ist eine Zeile, keine neue Katalogposition, und er erweitert den
   * Katalog auch nicht nachträglich.
   */
  catalogItemId: uuid.nullish(),
  description: z.string().min(1).max(500),
  quantity: z.number().positive().max(10000),
  unit: z.string().max(20),
  isExtra: z.boolean().default(false),
  pos: z.number().int().min(0).max(200).default(0)
})

export const rapportInputSchema = z.object({
  id: uuid,
  projectId: uuid,
  workDate: isoDate,
  description: z.string().max(4000).default(''),
  isRegie: z.boolean().default(false),
  lines: z.array(rapportLineSchema).max(100),
  signedByName: z.string().max(200).nullish(),
  photoIds: z.array(uuid).max(30).default([])
})
export type RapportInput = z.infer<typeof rapportInputSchema>

/**
 * Die Obergrenze gilt pro Monteur und Tag, nicht pro Rapport.
 *
 * Ein normaler Tag sieht so aus: 4 Stunden auf Projekt A, 2 auf Projekt B,
 * 2 Stunden Regie auf Projekt C. Das sind drei Rapporte, und jeder für sich
 * ist harmlos. Zusammen dürfen sie 16 Stunden nicht überschreiten.
 *
 * Diese Datei kann das nicht prüfen – sie sieht immer nur einen Rapport.
 * Die eigentliche Prüfung läuft deshalb auf dem Server über alle Rapporte
 * desselben Tages (routes/rapports.ts, pruefeTagesstunden).
 */
export const MAX_HOURS_PER_DAY = 16

/** Eine einzelne Stundenzeile über 16 ist ein Tippfehler, kein Arbeitstag. */
export const MAX_HOURS_PER_LINE = 16

/** Stundensumme eines Rapports. */
export function hoursIn(input: { lines: Array<{ kind: string; quantity: number }> }): number {
  return input.lines.filter((l) => l.kind === 'stunden').reduce((s, l) => s + l.quantity, 0)
}

export function validateRapportBusinessRules(
  input: RapportInput,
  today: string
): string[] {
  const problems: string[] = []
  if (input.workDate > today) problems.push('Das Datum liegt in der Zukunft.')

  for (const line of input.lines) {
    if (line.kind !== 'stunden') continue
    if (line.quantity > MAX_HOURS_PER_LINE) {
      problems.push(
        `"${line.description}": ${line.quantity} Stunden in einer Zeile. Mehr als ` +
          `${MAX_HOURS_PER_LINE} sind an einem Tag nicht möglich.`
      )
    }
  }

  // Nur die Stunden DIESES Rapports. Dass ein Monteur am selben Tag noch auf
  // zwei anderen Baustellen war, weiss hier niemand – das prüft der Server.
  if (hoursIn(input) > MAX_HOURS_PER_DAY) {
    problems.push(
      `Zusammen ${hoursIn(input)} Stunden in einem Rapport – mehr als ${MAX_HOURS_PER_DAY} ` +
        'sind an einem Tag nicht möglich.'
    )
  }
  if (input.lines.length === 0 && !input.description.trim()) {
    problems.push('Der Rapport ist leer.')
  }
  return problems
}
